import type { AdultGigRequestTeenDetailResponse } from "../../../types/gigTypes";
import { formatCityStateZip, formatDistanceLabel } from "../../../utils/LocationUtils";
import { formatTeenAge } from "./gigDisplay";

type TeenRequestProfileCardProps = {
  teenName: string;
  teenDetail?: AdultGigRequestTeenDetailResponse;
};

const TeenRequestProfileCard = ({ teenName, teenDetail }: TeenRequestProfileCardProps) => {
  if (!teenDetail) {
    return (
      <section className="min-w-0 rounded-[1.5rem] border border-myteengig-border bg-[linear-gradient(135deg,_rgba(219,234,254,0.9),_rgba(239,246,255,0.95))] p-4 sm:p-5">
        <p className="text-sm text-myteengig-text-muted">Loading or unavailable teen profile details.</p>
      </section>
    );
  }

  const locationParts = [
    formatCityStateZip(teenDetail, "Unknown"),
    formatDistanceLabel({
      distanceMiles: teenDetail.distanceMilesFromGig,
      zip: teenDetail.zip,
      comparisonZip: teenDetail.gigZip,
    }),
  ].filter(Boolean);

  const averageRatingLabel =
    typeof teenDetail.averageRating === "number" && teenDetail.totalReviews > 0
      ? teenDetail.averageRating.toFixed(1)
      : "New";
  const skills = teenDetail.skills ?? [];

  return (
    <section className="min-w-0 overflow-hidden rounded-[1.5rem] border border-sky-200 bg-[linear-gradient(135deg,_rgba(224,242,254,0.96),_rgba(239,246,255,0.98))] p-4 shadow-sm sm:p-5">
      <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">Teen Profile</p>
          <h3 className="mt-2 break-words text-xl font-semibold text-myteengig-text-primary">{teenName}</h3>
          <div className="mt-4 grid min-w-0 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-sky-200 bg-white/80 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Age</p>
              <p className="mt-1 font-medium text-myteengig-text-primary">{formatTeenAge(teenDetail.dateOfBirth)}</p>
            </div>
            <div className="min-w-0 rounded-2xl border border-sky-200 bg-white/80 px-4 py-3 sm:col-span-2">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Location</p>
              <p className="mt-1 break-words font-medium text-myteengig-text-primary">{locationParts.join(" • ")}</p>
            </div>
          </div>
          <div className="mt-4 grid min-w-0 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-sky-200 bg-white/80 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Rating</p>
              <p className="mt-1 font-medium text-myteengig-text-primary">{averageRatingLabel}</p>
            </div>
            <div className="rounded-2xl border border-sky-200 bg-white/80 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Reviews</p>
              <p className="mt-1 font-medium text-myteengig-text-primary">{teenDetail.totalReviews}</p>
            </div>
            <div className="rounded-2xl border border-sky-200 bg-white/80 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Completed gigs</p>
              <p className="mt-1 font-medium text-myteengig-text-primary">{teenDetail.completedGigCount}</p>
            </div>
          </div>
        </div>

        <div className="grid min-w-0 gap-3">
          <div className="min-w-0 rounded-2xl border border-sky-200 bg-white/80 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">About</p>
            <p className="mt-2 break-words text-sm leading-6 text-myteengig-text-primary">
              {teenDetail.bio?.trim() || "No self-description added yet."}
            </p>
          </div>
          <div className="min-w-0 rounded-2xl border border-sky-200 bg-white/80 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Skills</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {skills.length > 0 ? (
                skills.map((skill) => (
                  <span
                    key={skill}
                    className="rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-semibold text-myteengig-text-primary"
                  >
                    {skill}
                  </span>
                ))
              ) : (
                <p className="text-sm text-myteengig-text-primary">No skills added yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeenRequestProfileCard;
